import React, { useState, useEffect, useRef } from 'react';
import { Play, Info, Plus, Check, FolderSync, Sparkles, Globe, Film, Flame, Loader2, X } from 'lucide-react';
import { MediaRow } from '../components/MediaRow';
import { OnlineStreamModal } from '../components/OnlineStreamModal';
import {
  api,
  type HomeFeed,
  type MediaFile,
  type MoodFeed,
  type BecauseYouWatchedRow,
  type SmartCollection,
} from '../lib/api';
import { useThemeLanguage } from '../context/ThemeLanguageContext';
import { playbackLogger } from '../lib/playbackLogger';

export interface HomePageProps {
  onOpenMedia: (id: string) => void;
  onPlay: (file: MediaFile, title: string, subtitle?: string, initialTime?: number) => void;
  user?: any;
}

export function HomePage({ onOpenMedia, onPlay, user }: HomePageProps) {
  const { language, t, showToast } = useThemeLanguage();
  const [feed, setFeed] = useState<HomeFeed | null>(null);
  const [recommendations, setRecommendations] = useState<{ reason: string; reasonHe: string; items: any[] } | null>(null);
  const [becauseYouWatched, setBecauseYouWatched] = useState<BecauseYouWatchedRow[]>([]);
  const [collections, setCollections] = useState<SmartCollection[]>([]);
  const [activeVibe, setActiveVibe] = useState<string>('all');
  const [moodData, setMoodData] = useState<MoodFeed | null>(null);
  const [moodLoading, setMoodLoading] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const [isHeroPaused, setIsHeroPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [watchlistAnimating, setWatchlistAnimating] = useState(false);
  const [streamingHeroItem, setStreamingHeroItem] = useState<any | null>(null);
  const [showHeroTrailer, setShowHeroTrailer] = useState(false);
  const [heroTrailerKey, setHeroTrailerKey] = useState<string | null>(null);
  const [trailerLoading, setTrailerLoading] = useState(false);

  const loadFeed = async () => {
    try {
      const [data, recs, byw, cols] = await Promise.all([
        api.getHomeFeed(),
        api.getRecommendations().catch(() => null),
        api.getBecauseYouWatched().catch(() => []),
        api.getCollections().catch(() => []),
      ]);
      setFeed(data);
      if (recs && recs.items && recs.items.length > 0) {
        setRecommendations(recs);
      }
      if (byw && byw.length > 0) {
        setBecauseYouWatched(byw);
      }
      if (cols && cols.length > 0) {
        setCollections(cols);
      }
      if (data.hero) {
        const isHeroInWatchlist = Boolean(
          data.hero.inWatchlist ??
          (data.watchlist && data.watchlist.some((item) => item.id === data.hero?.id))
        );
        setInWatchlist(isHeroInWatchlist);
      } else {
        setInWatchlist(false);
      }
    } catch (err) {
      console.error('Failed to load feed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeed();
  }, [user?.id]);

  const carouselItems =
    feed?.heroCarousel && feed.heroCarousel.length > 0
      ? feed.heroCarousel
      : feed?.hero
      ? [feed.hero]
      : [];

  const hero = carouselItems[heroIndex] || feed?.hero;

  // Auto-rotate hero carousel every 8 seconds when not hovered and no stream/trailer is open
  useEffect(() => {
    if (isHeroPaused || streamingHeroItem || showHeroTrailer || carouselItems.length <= 1) return;
    const timer = setInterval(() => {
      setHeroIndex((prev) => {
        const next = (prev + 1) % carouselItems.length;
        playbackLogger.debug('CAROUSEL', 'Hero carousel rotated', {
          prevIndex: prev,
          nextIndex: next,
          title: carouselItems[next]?.title,
        });
        return next;
      });
    }, 8000);
    return () => clearInterval(timer);
  }, [isHeroPaused, streamingHeroItem, showHeroTrailer, carouselItems]);

  // Sync watchlist status and reset trailer when hero changes
  useEffect(() => {
    setHeroTrailerKey(null);
    setShowHeroTrailer(false);
    if (hero) {
      const isHeroInWatchlist = Boolean(
        hero.inWatchlist ??
        (feed?.watchlist && feed.watchlist.some((item) => item.id === hero?.id))
      );
      setInWatchlist(isHeroInWatchlist);
    }
  }, [heroIndex, hero?.id, feed?.watchlist]);

  const handleSelectMood = async (vibeId: string) => {
    setActiveVibe(vibeId);
    if (vibeId === 'all') {
      setMoodData(null);
      return;
    }
    setMoodLoading(true);
    try {
      const data = await api.getMood(vibeId);
      setMoodData(data);
    } catch (err) {
      console.error('Failed to load mood feed', err);
    } finally {
      setMoodLoading(false);
    }
  };

  const handleScan = async () => {
    setIsScanning(true);
    try {
      await api.triggerScan();
      setTimeout(async () => {
        await loadFeed();
        setIsScanning(false);
      }, 3000);
    } catch {
      setIsScanning(false);
    }
  };

  const handleHeroPlay = async () => {
    if (!hero) return;
    const heroSnapshot = hero;
    playbackLogger.info('MEDIA_NAV', 'User initiated hero play', {
      id: heroSnapshot.id,
      title: heroSnapshot.title,
      inLibrary: heroSnapshot.inLibrary,
    });
    try {
      const full = await api.getMedia(heroSnapshot.id);
      const file = full.files?.[0];
      if (file) {
        playbackLogger.info('MEDIA_NAV', 'Playing local media from hero', {
          id: heroSnapshot.id,
          title: full.title,
          fileId: file.id,
        });
        onPlay(file, full.title);
      } else {
        playbackLogger.info('MEDIA_NAV', 'No local file, opening online stream for hero snapshot', {
          id: heroSnapshot.id,
          title: heroSnapshot.title,
        });
        setStreamingHeroItem(heroSnapshot);
      }
    } catch {
      onOpenMedia(heroSnapshot.id);
    }
  };

  const handleHeroWatchlist = async () => {
    if (!hero) return;
    setWatchlistAnimating(true);
    setTimeout(() => setWatchlistAnimating(false), 500);
    const nextState = !inWatchlist;
    setInWatchlist(nextState);

    try {
      const res = await api.toggleWatchlist(hero.id);
      if (res && typeof res.inWatchlist === 'boolean') {
        setInWatchlist(res.inWatchlist);
        showToast(
          res.inWatchlist ? t('addedToWatchlist') : t('removedFromWatchlistToast'),
          'success'
        );
      }
    } catch (err) {
      // Revert optimistic update
      setInWatchlist(!nextState);
      showToast(
        language === 'he'
          ? 'יש להתחבר כדי לשמור ברשימת הצפייה'
          : 'Please sign in to save to your watchlist',
        'error'
      );
    }
  };

  const handleHeroTrailer = async () => {
    if (!hero) return;
    if (heroTrailerKey) {
      setShowHeroTrailer(true);
      return;
    }
    setTrailerLoading(true);
    try {
      const res = await api.getTrailer(hero.id, {
        title: hero.title,
        year: hero.year || undefined,
        tmdbId: hero.tmdbId,
      });
      if (res?.trailerKey) {
        setHeroTrailerKey(res.trailerKey);
        setShowHeroTrailer(true);
      } else {
        showToast(language === 'he' ? 'לא נמצא טריילר זמין לסרט זה' : 'No trailer found for this title', 'error');
      }
    } catch {
      showToast(language === 'he' ? 'שגיאה בטעינת טריילר' : 'Error loading trailer', 'error');
    } finally {
      setTrailerLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="relative min-h-screen bg-[#0F0F0F] text-white pb-16 overflow-hidden">
        {/* Hero Skeleton */}
        <div className="relative h-[80vh] md:h-[88vh] bg-neutral-900/60 flex items-end p-6 md:p-14 skeleton-shimmer">
          <div className="space-y-4 max-w-xl w-full">
            <div className="h-4 w-28 bg-white/10 rounded-full" />
            <div className="h-10 md:h-14 w-3/4 bg-white/10 rounded-2xl" />
            <div className="h-4 w-1/2 bg-white/10 rounded-full" />
            <div className="h-16 w-full bg-white/10 rounded-xl" />
            <div className="flex gap-3 pt-2">
              <div className="h-12 w-36 bg-white/20 rounded-xl" />
              <div className="h-12 w-36 bg-white/10 rounded-xl" />
            </div>
          </div>
        </div>

        {/* Rows Skeleton */}
        <div className="relative -mt-16 md:-mt-20 space-y-10 px-4 md:px-12">
          {[1, 2, 3].map((rowIndex) => (
            <div key={rowIndex} className="space-y-3">
              <div className="h-5 w-48 bg-white/10 rounded-full skeleton-shimmer" />
              <div className="flex gap-4 overflow-hidden">
                {[1, 2, 3, 4, 5, 6, 7].map((colIndex) => (
                  <div
                    key={colIndex}
                    className="shrink-0 w-[140px] md:w-[185px] aspect-[2/3] bg-neutral-900/80 border border-white/5 rounded-xl skeleton-shimmer"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#0F0F0F] text-white pb-16 home-page-container">
      {/* ─── Hero Section with Carousel & Ken Burns Animation ─── */}
      <section
        onMouseEnter={() => setIsHeroPaused(true)}
        onMouseLeave={() => setIsHeroPaused(false)}
        className="relative h-[82vh] md:h-[88vh] flex items-end overflow-hidden hero-section group/hero"
      >
        {/* Ambient Color Glow */}
        <div className="hero-ambient-glow" />

        {/* Backdrop Image or Gradient */}
        {hero?.backdropPath ? (
          <img
            key={hero.id}
            src={hero.backdropPath}
            alt={hero.title}
            className="absolute inset-0 w-full h-full object-cover object-center animate-ken-burns transition-opacity duration-1000"
          />
        ) : (
          <>
            <div className="absolute inset-0 bg-gradient-to-br from-[#1a0505] via-[#0F0F0F] to-[#0F0F0F] hero-gradient-dark" />
            <div className="absolute inset-0 bg-gradient-to-br from-white via-[#ffffff] to-[#f1f5f9] hero-gradient-light" />
          </>
        )}

        {/* Scrim behind hero text */}
        <div
          className={`absolute inset-0 hero-scrim-dark ${
            language === 'he'
              ? 'bg-gradient-to-l from-black/95 via-black/60 to-transparent'
              : 'bg-gradient-to-r from-black/95 via-black/60 to-transparent'
          }`}
        />
        <div
          className={`absolute inset-0 hero-scrim-light ${
            language === 'he'
              ? 'bg-gradient-to-l from-white/95 via-white/75 to-transparent'
              : 'bg-gradient-to-r from-white/95 via-white/75 to-transparent'
          }`}
        />

        {/* Cinematic Bottom Gradients */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/80 via-25% to-transparent hero-gradient-dark pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#ffffff] from-5% via-[#ffffff]/95 via-25% to-transparent hero-gradient-light pointer-events-none" />

        {/* Carousel Indicators (dots on bottom-left in RTL / bottom-right in LTR) */}
        {carouselItems.length > 1 && (
          <div
            className={`absolute bottom-20 md:bottom-28 z-20 flex items-center gap-2 bg-black/60 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10 shadow-xl transition-all duration-300 ${
              language === 'he' ? 'left-6 md:left-14' : 'right-6 md:right-14'
            }`}
          >
            {carouselItems.map((item, idx) => (
              <button
                key={item.id}
                onClick={() => setHeroIndex(idx)}
                className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  idx === heroIndex ? 'w-6 bg-accent-primary' : 'w-2 bg-white/40 hover:bg-white/80'
                }`}
                aria-label={`Slide ${idx + 1}`}
              />
            ))}
          </div>
        )}

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-[1920px] mx-auto px-4 md:px-12 pb-16 md:pb-24">
          <div className="max-w-2xl space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-black tracking-widest text-accent-primary uppercase drop-shadow">
                H O M E F L I X
              </span>
              {hero?.rating && (
                <span className="flex items-center gap-1 text-xs font-bold text-emerald-400 bg-emerald-950/70 px-2 py-0.5 rounded border border-emerald-500/40 backdrop-blur-md shadow">
                  <Sparkles className="w-3 h-3" />
                  {(hero.rating * 10).toFixed(0)}% Match
                </span>
              )}
              <span className="text-xs font-black text-white bg-black/60 px-2 py-0.5 rounded backdrop-blur-md border border-white/20 shadow">
                4K ULTRA HD
              </span>
              <span className="text-xs font-semibold text-neutral-300 bg-black/60 px-2 py-0.5 rounded backdrop-blur-md border border-white/10 shadow">
                HDR10+
              </span>
              <span className="text-xs font-semibold text-neutral-300 bg-black/60 px-2 py-0.5 rounded backdrop-blur-md border border-white/10 shadow">
                DOLBY 5.1
              </span>
              {hero?.inLibrary && (
                <span className="text-xs font-medium text-white bg-accent-primary/80 px-2 py-0.5 rounded backdrop-blur-md border border-accent-primary shadow hero-badge-library">
                  {language === 'he' ? 'בספרייה המקומית' : 'In Local Library'}
                </span>
              )}
            </div>

            {(() => {
              const isHebrew = language === 'he';
              const hasHebrew = Boolean(hero?.titleHe);
              const englishTitle = hero?.title || hero?.originalTitle || 'Homeflix';
              const hasBothTitles = Boolean(hasHebrew && englishTitle && hero?.titleHe !== englishTitle);

              const displayTitle = isHebrew
                ? (hero?.titleHe || englishTitle)
                : (englishTitle || hero?.titleHe);

              const displaySubtitle = hasBothTitles
                ? (isHebrew ? englishTitle : hero?.titleHe)
                : null;

              const titleDir = isHebrew ? (hero?.titleHe ? 'rtl' : 'ltr') : 'ltr';
              const subtitleDir = isHebrew ? 'ltr' : 'rtl';

              return (
                <div>
                  <h1
                    className="text-3xl sm:text-4xl md:text-6xl font-black tracking-tight drop-shadow-[0_4px_16px_rgba(0,0,0,0.95)] text-white"
                    dir={titleDir}
                  >
                    {displayTitle}
                  </h1>
                  {displaySubtitle && (
                    <p
                      className="text-lg md:text-2xl text-neutral-200 font-semibold drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)] mt-1"
                      dir={subtitleDir}
                    >
                      {displaySubtitle}
                    </p>
                  )}
                </div>
              );
            })()}

            {hero?.tagline && (
              <p className="text-sm md:text-base italic text-neutral-200 drop-shadow-[0_1px_6px_rgba(0,0,0,0.85)]">
                "{hero.tagline}"
              </p>
            )}

            <p className="text-sm md:text-base text-neutral-200 leading-relaxed max-w-lg line-clamp-3 drop-shadow-[0_1px_6px_rgba(0,0,0,0.85)]">
              {language === 'he'
                ? (hero?.overviewHe || hero?.overview || 'ברוכים הבאים ל-Homeflix. קולנוע ביתי פרטי עם אלפי סרטים וסדרות לצפייה ישירה מיידית או הורדה באיכות גבוהה.')
                : (hero?.overview || 'Welcome to Homeflix. Your private cinema with thousands of movies and series for instant online streaming or high-speed downloading.')}
            </p>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              {hero ? (
                <>
                  {hero.inLibrary && (
                    <button
                      onClick={handleHeroPlay}
                      className="flex items-center gap-2 rounded-lg bg-white px-7 py-3 text-sm font-bold text-black transition-all hover:bg-neutral-200 active:scale-95 shadow-2xl cursor-pointer btn-interactive hero-btn-play btn-play-pulse"
                    >
                      <Play className="h-5 w-5 fill-current" />
                      {t('playLocal')}
                    </button>
                  )}

                  {/* Direct Online Streaming Button */}
                  <button
                    onClick={() => {
                      if (hero) {
                        playbackLogger.info('ONLINE_STREAM', 'Opened online stream modal from hero banner', {
                          id: hero.id,
                          tmdbId: hero.tmdbId,
                          title: hero.title,
                        });
                        setStreamingHeroItem(hero);
                      }
                    }}
                    className="flex items-center gap-2 rounded-lg bg-accent-primary px-6 py-3 text-sm font-bold text-white transition-all hover:brightness-110 active:scale-95 shadow-xl shadow-accent-glow cursor-pointer btn-interactive hero-btn-stream"
                  >
                    <Globe className="h-5 w-5" />
                    {t('watchOnline')}
                  </button>

                  {/* Direct Trailer Button */}
                  <button
                    onClick={handleHeroTrailer}
                    disabled={trailerLoading}
                    className="flex items-center gap-2 rounded-lg bg-neutral-800/90 hover:bg-neutral-700 px-5 py-3 text-sm font-bold text-white backdrop-blur-md transition-all active:scale-95 border border-white/20 cursor-pointer btn-interactive shadow-lg"
                    title={language === 'he' ? 'צפה בטריילר הרשמי' : 'Watch Official Trailer'}
                  >
                    {trailerLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin text-accent-primary" />
                    ) : (
                      <Film className="h-5 w-5 text-accent-primary" />
                    )}
                    {language === 'he' ? 'טריילר' : 'Trailer'}
                  </button>

                  <button
                    onClick={() => onOpenMedia(hero.id)}
                    className="flex items-center gap-2 rounded-lg bg-black/40 hover:bg-black/60 px-6 py-3 text-sm font-semibold text-white backdrop-blur-md transition-all active:scale-95 border border-white/20 cursor-pointer btn-interactive hero-btn-details shadow-xl"
                  >
                    <Info className="h-5 w-5" />
                    {t('moreInfo')}
                  </button>

                  <button
                    onClick={handleHeroWatchlist}
                    className={`flex items-center justify-center rounded-full h-11 w-11 border transition-all backdrop-blur-md cursor-pointer btn-interactive active:scale-90 hero-btn-watchlist ${
                      inWatchlist
                        ? 'border-emerald-500/50 bg-emerald-500/30 text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.4)]'
                        : 'border-white/30 bg-black/50 text-white hover:border-white hover:bg-black/70 shadow-lg'
                    }`}
                    title={inWatchlist ? t('inWatchlist') : t('addToWatchlist')}
                  >
                    {inWatchlist ? (
                      <Check className={`h-5 w-5 text-emerald-400 ${watchlistAnimating ? 'animate-check-pop' : ''}`} />
                    ) : (
                      <Plus className="h-5 w-5 text-white" />
                    )}
                  </button>
                </>
              ) : user?.role === 'admin' ? (
                <button
                  onClick={handleScan}
                  disabled={isScanning}
                  className="flex items-center gap-2 rounded-lg bg-accent-primary px-6 py-3 text-sm font-bold text-white transition-all hover:brightness-110 shadow-lg shadow-accent-glow cursor-pointer"
                >
                  <FolderSync className={`h-5 w-5 ${isScanning ? 'animate-spin' : ''}`} />
                  {isScanning ? t('scanning') : t('scanLibrary')}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Content Section (Overlaps hero bottom gradient seamlessly) ─── */}
      <div className="relative z-20 -mt-8 md:-mt-12 space-y-6 md:space-y-8 pb-20 content-rows-container">
        {/* Interactive Mood & Vibe Selector ("מה בא לך לראות הערב?") */}
        <section className="px-4 md:px-12">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[#E50914]" />
            <h3 className="text-sm md:text-base font-extrabold text-white tracking-tight">
              {language === 'he' ? 'מה מצב הרוח שלך הערב?' : 'What are you in the mood for?'}
            </h3>
          </div>
          <div className="flex items-center gap-2.5 overflow-x-auto pt-3 pb-3.5 px-1.5 -mx-1.5 -my-1 scrollbar-hide">
            {[
              { id: 'all', emoji: '✨', labelHe: 'הכל', labelEn: 'All' },
              { id: 'adrenaline', emoji: '⚡', labelHe: 'אדרנלין ומתח', labelEn: 'Action & Thrills' },
              { id: 'funny', emoji: '🍿', labelHe: 'קליל ומצחיק', labelEn: 'Feel-Good & Comedy' },
              { id: 'mindbend', emoji: '🧠', labelHe: 'מסתורין ומד״ב', labelEn: 'Mind-Benders' },
              { id: 'heartfelt', emoji: '🕯️', labelHe: 'דרמה ורגש', labelEn: 'Heartfelt Drama' },
              { id: 'family', emoji: '👨‍👩‍👧‍👦', labelHe: 'בילוי משפחתי', labelEn: 'Family Night' },
              { id: 'binge', emoji: '🔥', labelHe: 'בינג׳ לסופ״ש', labelEn: 'Weekend Binge' },
            ].map((vibe) => (
              <button
                key={vibe.id}
                onClick={() => handleSelectMood(vibe.id)}
                className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-bold mood-pill cursor-pointer relative ${
                  activeVibe === vibe.id
                    ? 'mood-pill-active z-10'
                    : 'bg-neutral-900/80 text-neutral-300 border-white/10 hover:border-white/30 hover:bg-neutral-800 hover:z-10'
                }`}
              >
                <span>{vibe.emoji}</span>
                <span>{language === 'he' ? vibe.labelHe : vibe.labelEn}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Dynamic Curated Mood Block (If user clicked a vibe) */}
        {activeVibe !== 'all' && moodData && moodData.items.length > 0 && (
          <div className="mx-4 md:mx-12 p-4 md:p-6 rounded-2xl bg-gradient-to-r from-red-950/20 via-black to-neutral-900 border border-red-500/20 shadow-2xl animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xl">{moodData.emoji}</span>
                  <h2 className="text-xl md:text-2xl font-black text-white">
                    {language === 'he' ? moodData.titleHe : moodData.title}
                  </h2>
                </div>
                <p className="text-xs md:text-sm text-neutral-400 mt-1">
                  {language === 'he' ? moodData.descriptionHe : moodData.description}
                </p>
              </div>
              <button
                onClick={() => setActiveVibe('all')}
                className="text-xs text-neutral-400 hover:text-white px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all cursor-pointer"
              >
                {language === 'he' ? 'חזור להכל' : 'Reset'}
              </button>
            </div>
            <MediaRow
              title=""
              items={moodData.items}
              onSelect={onOpenMedia}
            />
          </div>
        )}
        {/* Continue Watching */}
        {feed?.continueWatching && feed.continueWatching.length > 0 && (
          <MediaRow
            title={t('continueWatching')}
            items={feed.continueWatching}
            isContinueWatching
            onSelect={onOpenMedia}
            onPlay={async (id) => {
              const full = await api.getMedia(id);
              const file = full.files?.[0];
              const cwItem = feed.continueWatching.find((c: any) => (c.mediaItemId || c.id) === id);
              const initTime = cwItem?.currentTime || 0;
              if (file) onPlay(file, full.title, undefined, initTime);
              else onOpenMedia(id);
            }}
          />
        )}

        {/* Local Library (if any items) */}
        {feed?.inLibrary && feed.inLibrary.length > 0 && (
          <MediaRow
            title={t('inLibrary')}
            items={feed.inLibrary}
            onSelect={onOpenMedia}
          />
        )}

        {/* Personal Recommendations */}
        {recommendations && recommendations.items && recommendations.items.length > 0 && (
          <MediaRow
            title={language === 'he' ? (recommendations.reasonHe || 'מומלץ במיוחד עבורך') : (recommendations.reason || 'Recommended For You')}
            items={recommendations.items}
            onSelect={onOpenMedia}
          />
        )}

        {/* "Because You Watched..." Deep Personalized Recommendation Rows */}
        {becauseYouWatched.map((row) => (
          <MediaRow
            key={row.sourceId}
            title={language === 'he' ? row.reasonHe : row.reason}
            items={row.items}
            onSelect={onOpenMedia}
          />
        ))}

        {/* Curated Franchises & Collections Row */}
        {collections && collections.length > 0 && (
          <section className="relative px-4 md:px-12 my-6">
            <div className="flex items-center gap-2 mb-3">
              <Film className="w-5 h-5 text-[#E50914]" />
              <h2 className="text-lg md:text-xl font-extrabold text-white tracking-tight drop-shadow">
                {language === 'he' ? 'קולקציות וסאגות קולנועיות' : 'Curated Franchises & Sagas'}
              </h2>
            </div>
            <div className="flex gap-4 overflow-x-auto pt-4 pb-6 px-2 md:px-4 -mx-2 md:-mx-4 -mt-2 -mb-3 scrollbar-hide">
              {collections.map((col) => (
                <div
                  key={col.id}
                  onClick={() => {
                    if (col.items?.[0]) onOpenMedia(col.items[0].id);
                  }}
                  className="shrink-0 w-[240px] md:w-[320px] aspect-video rounded-2xl overflow-hidden bg-neutral-900 border border-white/10 hover:border-red-500/50 cursor-pointer transition-all duration-300 hover:scale-105 shadow-xl group/col relative hover:z-20"
                >
                  <img
                    src={col.backdropPath || col.posterPath || 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=800'}
                    alt={col.name}
                    className="w-full h-full object-cover group-hover/col:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent flex flex-col justify-end p-4">
                    <span className="text-[10px] font-black tracking-widest text-[#E50914] uppercase drop-shadow">
                      {col.itemsCount} {language === 'he' ? 'סרטים' : 'MOVIES'}
                    </span>
                    <h3 className="text-base font-bold text-white drop-shadow line-clamp-1">
                      {col.name}
                    </h3>
                    {col.overview && (
                      <p className="text-xs text-neutral-300 line-clamp-1 mt-0.5">
                        {col.overview}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Top 10 Today in Homeflix (Numbered 1-20) */}
        {feed?.topRated && feed.topRated.length > 0 && (
          <MediaRow
            title={language === 'he' ? 'ה-Top 10 ב-Homeflix היום' : 'Top 10 on Homeflix Today'}
            items={feed.topRated.slice(0, 10)}
            numbered
            onSelect={onOpenMedia}
          />
        )}

        {/* Trending Movies */}
        {feed?.trendingMovies && feed.trendingMovies.length > 0 && (
          <MediaRow
            title={t('trendingMovies')}
            items={feed.trendingMovies}
            onSelect={onOpenMedia}
          />
        )}

        {/* Trending TV Series */}
        {feed?.trendingShows && feed.trendingShows.length > 0 && (
          <MediaRow
            title={t('trendingShows')}
            items={feed.trendingShows}
            onSelect={onOpenMedia}
          />
        )}

        {/* Action & Adventure */}
        {feed?.actionMovies && feed.actionMovies.length > 0 && (
          <MediaRow
            title={t('actionMovies')}
            items={feed.actionMovies}
            onSelect={onOpenMedia}
          />
        )}

        {/* Sci-Fi & Fantasy */}
        {feed?.scifiMovies && feed.scifiMovies.length > 0 && (
          <MediaRow
            title={t('scifiMovies')}
            items={feed.scifiMovies}
            onSelect={onOpenMedia}
          />
        )}

        {/* Comedy Hits */}
        {feed?.comedyMovies && feed.comedyMovies.length > 0 && (
          <MediaRow
            title={t('comedyMovies')}
            items={feed.comedyMovies}
            onSelect={onOpenMedia}
          />
        )}

        {/* Animation & Family */}
        {feed?.animationMovies && feed.animationMovies.length > 0 && (
          <MediaRow
            title={t('animationMovies')}
            items={feed.animationMovies}
            onSelect={onOpenMedia}
          />
        )}

        {/* Thriller & Horror */}
        {feed?.thrillerMovies && feed.thrillerMovies.length > 0 && (
          <MediaRow
            title={t('thrillerMovies')}
            items={feed.thrillerMovies}
            onSelect={onOpenMedia}
          />
        )}

        {/* Popular Shows */}
        {feed?.popularShows && feed.popularShows.length > 0 && (
          <MediaRow
            title={t('popularShows')}
            items={feed.popularShows}
            onSelect={onOpenMedia}
          />
        )}

        {/* My List */}
        {feed?.watchlist && feed.watchlist.length > 0 && (
          <MediaRow
            title={t('myList')}
            items={feed.watchlist}
            onSelect={onOpenMedia}
          />
        )}
      </div>

      {/* Hero Direct Stream Modal — firmly locked to selected snapshot */}
      {streamingHeroItem && (
        <OnlineStreamModal
          title={language === 'he' && streamingHeroItem.titleHe ? streamingHeroItem.titleHe : (streamingHeroItem.title || streamingHeroItem.originalTitle || 'Movie')}
          tmdbId={streamingHeroItem.tmdbId}
          imdbId={streamingHeroItem.imdbId}
          type={streamingHeroItem.type}
          onClose={() => {
            playbackLogger.info('ONLINE_STREAM', 'Closed online stream modal from hero banner', {
              id: streamingHeroItem.id,
              title: streamingHeroItem.title,
            });
            setStreamingHeroItem(null);
          }}
        />
      )}

      {/* Hero Trailer Modal */}
      {showHeroTrailer && heroTrailerKey && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 animate-modal-backdrop">
          <div className="relative w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl border border-white/20 animate-modal-sheet">
            <button
              onClick={() => setShowHeroTrailer(false)}
              className="absolute top-3 right-3 z-30 p-2.5 rounded-full bg-black/70 hover:bg-black text-white transition-all cursor-pointer border border-white/20 hover:scale-105"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${heroTrailerKey}?autoplay=1&controls=1&rel=0`}
              title="Hero Trailer"
              allow="autoplay; encrypted-media; fullscreen"
              allowFullScreen
              className="w-full h-full border-0"
            />
          </div>
        </div>
      )}
    </div>
  );
}
